import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * What the user chose, which is not the same as what they are looking at:
 * `system` resolves to one of the other two and follows the OS from then on.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

/** The theme actually on the screen. */
export type ResolvedTheme = 'light' | 'dark';

/**
 * Where the choice is kept, and the name the pre-paint script in each app's
 * index.html reads. Change one and change both — the only cost of getting it
 * wrong is a flash of the wrong theme on the first paint, which is silent.
 */
export const THEME_STORAGE_KEY = 'iqd-theme';

/** Off unless asked for. A signed-out first visit is the light app. */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isPreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Private browsing, and some managed browsers, throw on `localStorage` rather
 * than returning nothing. The theme is a convenience — it is never worth an
 * exception on the way to the first paint.
 */
function readStoredPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

function storePreference(preference: ThemePreference) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Nothing to do: the theme lasts the session instead of outliving it.
  }
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference;
}

type ThemeContextValue = {
  /** The choice, `system` included. What a settings control should show. */
  preference: ThemePreference;
  /** The theme on the screen. What a component branching on dark should read. */
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  preference: DEFAULT_THEME_PREFERENCE,
  resolved: 'light',
  setPreference: () => undefined,
});

/**
 * Holds the theme, and writes it where the stylesheet can see it.
 *
 * The attribute is always explicit — `light` or `dark`, never absent — because
 * `theme.css` keys dark off `[data-theme='dark']` and nothing else. A document
 * that carries no attribute is therefore light, which is what the print
 * windows (written by `document.write`, with no root attribute of their own)
 * should be.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [systemPreference, setSystemPreference] = useState<ResolvedTheme>(systemTheme);

  // Only while following the system, so a user on an explicit theme is not
  // re-rendered every time their machine crosses sunset.
  useEffect(() => {
    if (preference !== 'system') return undefined;

    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => setSystemPreference(query.matches ? 'dark' : 'light');
    onChange();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference]);

  const resolved: ResolvedTheme = preference === 'system' ? systemPreference : preference;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset['theme'] = resolved;
    // Native chrome the stylesheet does not reach: scrollbars, date pickers,
    // the caret. `color-scheme` is also set in theme.css, but the attribute is
    // what the rest of the app keys off, so keep the two written together.
    root.style.colorScheme = resolved;
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    storePreference(next);
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
